import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { useNavigate } from "react-router-dom";

const ControlsByTypeChart = ({ data = [], permits = {}, appliedFilters = {}, assets = [] }) => {
  const navigate = useNavigate();

  const canShowData =
    permits.isSuperuser || (permits.hasAnyRole && permits.canControlsRead);

  const fixedTypes = ["Correctivo", "Preventivo", "Detectivo"];

  // Convertir data a array uniforme {name, value}
  const chartDataArray = useMemo(() => {
    if (!canShowData) {
      return [
        { name: "Correctivo", value: 8 },
        { name: "Preventivo", value: 28 },
        { name: "Detectivo", value: 11 },
      ];
    }

    if (Array.isArray(data)) return data;

    return Object.entries(data).map(([name, value]) => ({ name, value }));
  }, [data, canShowData]);

  const series = useMemo(
    () =>
      fixedTypes.map((type) => {
        const found = chartDataArray.find((item) => item.name === type);
        return {
          name: type,
          data: [found ? found.value : 0],
        };
      }),
    [chartDataArray]
  );

  const colors = canShowData
    ? ["#E74C3C", "#3498DB", "#F39C12"]
    : ["#CCCCCC", "#AAAAAA", "#888888"];

  const maxValue = useMemo(() => {
    const allValues = series.flatMap((s) => s.data);
    return Math.max(...allValues, 0);
  }, [series]);

  // Función para construir URL con filtros del Dashboard
  const buildNavigationUrl = (type) => {
    const params = new URLSearchParams();
    
    // Agregar parámetro de tipo del gráfico
    params.append('type', type);
    
    // Agregar filtros de activos del Dashboard
    if (appliedFilters.asset && appliedFilters.asset.length > 0) {
      const assetNames = appliedFilters.asset
        .map(code => assets.find(a => a.code === code)?.name)
        .filter(Boolean);
      
      if (assetNames.length > 0) {
        params.append('assets', assetNames.join(','));
      }
    }
    
    // Agregar filtros de áreas del Dashboard
    if (appliedFilters.area && appliedFilters.area.length > 0) {
      const allFilterAreas = assets.reduce((acc, asset) => {
        if (asset.filter_areas) {
          asset.filter_areas.forEach(area => {
            if (!acc.find(a => a.id === area.id)) {
              acc.push(area);
            }
          });
        }
        return acc;
      }, []);

      const areaNames = appliedFilters.area
        .map(id => allFilterAreas.find(a => a.id === id)?.name)
        .filter(Boolean);
      
      if (areaNames.length > 0) {
        params.append('areas', areaNames.join(','));
      }
    }
    
    return `/controls?${params.toString()}`;
  };

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            if (!canShowData) return;
            const type = fixedTypes[config.seriesIndex];
            navigate(buildNavigationUrl(type)); // ✅ Navega con filtros
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "70%",
          borderRadius: 6,
        },
      },
      xaxis: {
        categories: ["Tipo"],
        labels: { show: false },
      },
      yaxis: {
        min: 0,
        max: Math.ceil(maxValue),
        forceNiceScale: true,
        labels: {
          formatter: (val) => val.toFixed(0),
        },
      },
      legend: { show: true, onItemClick: { toggleDataSeries: true } },
      dataLabels: { enabled: true },
      colors,
      tooltip: { y: { formatter: (val) => val ?? 0 } },
    }),
    [canShowData, colors, maxValue, appliedFilters, assets] // ✅ Dependencias actualizadas
  );

  return (
    <div
      style={{
        position: "relative",
        cursor: canShowData ? "default" : "not-allowed",
      }}
    >
      <Chart options={options} series={series} type="bar" height={300} />
      {!canShowData && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            fontWeight: "bold",
            color: "#555",
          }}
        >
          No tienes permisos para ver este gráfico
        </div>
      )}
    </div>
  );
};

export default ControlsByTypeChart;